import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  BarChart3,
  Calendar,
  Trophy,
  Users
} from 'lucide-react';
import { getUserRevenue, getRevenueLeaderboard } from '../services/api';
import { RevenueTracking } from '../types';
import { LoadingSpinner, SkeletonCard } from './LoadingStates';
import { NoRevenueDataEmpty } from './EmptyStates';

interface RevenueDisplayProps {
  userId: number;
  showComparison?: boolean;
  className?: string;
}

interface LeaderboardEntry {
  user_id: number;
  name: string;
  gross_revenue: number;
  pay_amount?: number;
  rev_pay_ratio?: number;
  rank: number;
}

const RevenueDisplay: React.FC<RevenueDisplayProps> = ({
  userId,
  showComparison = true,
  className = ''
}) => {
  const [revenueData, setRevenueData] = useState<RevenueTracking[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

  useEffect(() => {
    fetchData();
  }, [userId, selectedPeriod]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [revenueResponse, leaderboardResponse] = await Promise.all([
        getUserRevenue(userId),
        showComparison ? getRevenueLeaderboard(selectedPeriod) : Promise.resolve({ data: [] })
      ]);

      setRevenueData(revenueResponse.data.revenue_history || []);
      setLeaderboard(leaderboardResponse.data || []);
    } catch (err: any) {
      console.error('Error fetching revenue data:', err);
      setError(err.response?.data?.message || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-900/30 border border-red-500/30 rounded-lg p-4 ${className}`}>
        <div className="text-red-400">
          Failed to load revenue data: {error}
        </div>
      </div>
    );
  }

  if (revenueData.length === 0) {
    return (
      <div className={className}>
        <NoRevenueDataEmpty />
      </div>
    );
  }

  // Calculate metrics
  const sortedData = [...(revenueData || [])].sort((a, b) => 
    new Date(b.month).getTime() - new Date(a.month).getTime()
  );

  const latestMonth = sortedData[0];
  const previousMonth = sortedData[1];
  
  const totalRevenue = (revenueData || []).reduce((sum, item) => sum + (item.gross_revenue || 0), 0);
  const averageRevenue = totalRevenue / (revenueData?.length || 1);
  
  const revenueChange = latestMonth && previousMonth 
    ? ((latestMonth.gross_revenue || 0) - (previousMonth.gross_revenue || 0)) / (previousMonth.gross_revenue || 1) * 100
    : 0;

  const userRank = leaderboard.findIndex(entry => entry.user_id === userId) + 1;

  // Prepare chart data (last 6 months)
  const chartData = sortedData.slice(0, 6).reverse();
  const maxRevenue = Math.max(...chartData.map(item => item.gross_revenue || 0));

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Revenue Overview */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <span>Revenue Overview</span>
          </h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-white text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Latest Month */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Latest Month</span>
              <DollarSign className="w-4 h-4 text-green-400" />
            </div>
            <div className="text-2xl font-bold text-white">
              ${(latestMonth?.gross_revenue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">
              {latestMonth ? new Date(latestMonth.month).toLocaleDateString('en-US', { 
                month: 'short', 
                year: 'numeric' 
              }) : 'No data'}
            </div>
          </div>

          {/* Revenue Change */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Month Change</span>
              {revenueChange >= 0 ? (
                <TrendingUp className="w-4 h-4 text-green-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
            </div>
            <div className={`text-2xl font-bold ${
              revenueChange >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {revenueChange >= 0 ? '+' : ''}{revenueChange.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-400">vs previous month</div>
          </div>

          {/* Average Revenue */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Average</span>
              <BarChart3 className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-white">
              ${averageRevenue.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">
              {revenueData?.length || 0} months
            </div>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-300 mb-3">
            Revenue Trend (Last 6 Months)
          </h4>
          <div className="flex items-end space-x-2 h-32">
            {chartData.map((item, index) => {
              const height = maxRevenue > 0 ? ((item.gross_revenue || 0) / maxRevenue) * 100 : 0;
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-blue-600 rounded-t min-h-[4px] transition-all duration-1000 ease-out"
                    style={{ height: `${height}%` }}
                  />
                  <div className="text-xs text-gray-400 mt-1 transform -rotate-45 origin-center">
                    {new Date(item.month).toLocaleDateString('en-US', { 
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rev/Pay Ratio (if available) */}
        {latestMonth?.rev_pay_ratio && (
          <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-purple-300">Revenue/Pay Ratio:</span>
              <span className="text-lg font-semibold text-white">
                {Number(latestMonth.rev_pay_ratio).toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Leaderboard Comparison */}
      {showComparison && leaderboard.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2 mb-4">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <span>Leaderboard Position</span>
          </h3>

          {userRank > 0 ? (
            <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    userRank === 1 ? 'bg-yellow-600 text-yellow-100' :
                    userRank === 2 ? 'bg-gray-500 text-gray-100' :
                    userRank === 3 ? 'bg-orange-600 text-orange-100' :
                    'bg-gray-600 text-gray-300'
                  }`}>
                    #{userRank}
                  </div>
                  <span className="text-white font-medium">Your Position</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">
                    ${(leaderboard[userRank - 1]?.gross_revenue || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">
                    {selectedPeriod} revenue
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
              <div className="text-gray-400 text-center">
                No ranking data available for this period
              </div>
            </div>
          )}

          {/* Top 5 Leaderboard */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span>Top Performers</span>
            </h4>
            {leaderboard.slice(0, 5).map((entry, index) => (
              <div 
                key={entry.user_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  entry.user_id === userId 
                    ? 'bg-blue-900/30 border border-blue-500/30' 
                    : 'bg-gray-700/30'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-600 text-yellow-100' :
                    index === 1 ? 'bg-gray-500 text-gray-100' :
                    index === 2 ? 'bg-orange-600 text-orange-100' :
                    'bg-gray-600 text-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <span className={`${
                    entry.user_id === userId ? 'text-blue-300 font-medium' : 'text-gray-300'
                  }`}>
                    {entry.name}
                    {entry.user_id === userId && ' (You)'}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-white font-medium">
                    ${entry.gross_revenue.toLocaleString()}
                  </div>
                  {entry.rev_pay_ratio && (
                    <div className="text-xs text-gray-400">
                      Ratio: {Number(entry.rev_pay_ratio).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueDisplay;
